import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Clock, Wind, Droplets } from "lucide-react";
import { getPlaceToday, getLocalEvents } from "@/lib/api";
import { T } from "@/context/LanguageContext";

const WIcon = ({ name, className }) => {
  const key = (name || "cloud").split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join("");
  const C = Icons[key] || Icons.Cloud;
  return <C className={className} strokeWidth={1.6} />;
};

// Live wall-clock for the place, derived from its UTC offset (ticks every second).
function useLocalClock(offsetSeconds) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (offsetSeconds == null) return null;
  const d = new Date(now + new Date(now).getTimezoneOffset() * 60000 + offsetSeconds * 1000);
  return d;
}

export function PlaceToday({ geoId, chips = [], variant = "panel" }) {
  const [today, setToday] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [events, setEvents] = useState(null);

  useEffect(() => {
    let ok = true;
    setLoaded(false); setToday(null); setEvents(null);
    getPlaceToday(geoId).then((d) => { if (ok) { setToday(d); setLoaded(true); } }).catch(() => ok && setLoaded(true));
    getLocalEvents(geoId, 3).then((d) => { if (ok) setEvents(d.items || []); }).catch(() => ok && setEvents([]));
    return () => { ok = false; };
  }, [geoId]);

  const clock = useLocalClock(today?.utc_offset_seconds);
  const timeStr = clock ? clock.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) : null;
  const dateStr = clock ? clock.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) : null;
  const wx = today?.available ? today : null;

  if (variant === "hero") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
        className="rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-md"
        data-testid="place-today"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-oldglory-red/70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-oldglory-red" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-widest text-white/80"><T>Right Now</T></span>
        </div>

        <div className="flex items-end justify-between gap-4">
          <div data-testid="place-today-time">
            <div className="font-display text-3xl font-black leading-none text-white tabular-nums whitespace-nowrap">{timeStr || "—"}</div>
            <div className="mt-1 text-xs text-white/70">{dateStr || <T>Local time</T>}</div>
          </div>
          <div className="text-right" data-testid="place-today-weather">
            {wx ? (
              <>
                <div className="flex items-center justify-end gap-2">
                  <WIcon name={wx.icon} className="h-8 w-8 text-white" />
                  <span className="font-display text-3xl font-black leading-none text-white">{wx.temp_f}°</span>
                </div>
                <div className="mt-1 text-xs font-semibold text-white/85"><T>{wx.condition}</T></div>
              </>
            ) : (
              <div className="text-xs text-white/60">{loaded ? "" : <T>Loading…</T>}</div>
            )}
          </div>
        </div>

        {wx && (
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-white/10 pt-3 text-xs text-white/75">
            {wx.high_f != null && <span className="font-semibold text-white">H {wx.high_f}° · L {wx.low_f}°</span>}
            {wx.humidity != null && <span className="inline-flex items-center gap-1"><Droplets className="h-3.5 w-3.5" /> {wx.humidity}%</span>}
            {wx.wind_mph != null && <span className="inline-flex items-center gap-1"><Wind className="h-3.5 w-3.5" /> {wx.wind_mph} mph</span>}
          </div>
        )}

        {events && events.length > 0 && (
          <div className="mt-3 border-t border-white/10 pt-3" data-testid="place-today-events">
            <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-white/70">
              <Icons.CalendarDays className="h-3.5 w-3.5 text-white/80" /> <T>This Weekend</T>
            </div>
            <ul className="space-y-1">
              {events.slice(0, 2).map((e, i) => (
                <li key={i}>
                  <a href={e.link} target="_blank" rel="noopener noreferrer" className="group flex items-start gap-1.5 text-[13px] text-white/85 hover:text-white" data-testid={`today-event-${i}`}>
                    <Icons.ArrowUpRight className="mt-0.5 h-3 w-3 shrink-0 text-white/50 group-hover:text-white" />
                    <span className="line-clamp-1">{e.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {chips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="place-today-chips">
            {chips.map((c) => (
              <button key={c.id} onClick={c.onClick}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 transition-colors hover:bg-white hover:text-navy"
                data-testid={`today-chip-${c.id}`}>
                {c.icon}<T>{c.label}</T>
              </button>
            ))}
          </div>
        )}
        {wx?.timezone && <p className="mt-2 text-[10px] text-white/50">{wx.timezone} · <a href="https://open-meteo.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/80">Open-Meteo</a></p>}
      </motion.div>
    );
  }

  // Fallback light "panel" variant (unused by the hero layout, kept for reuse)
  return (
    <section className="rounded-2xl border border-border bg-parchment-light/70 p-4 shadow-sm sm:p-5" data-testid="place-today">
      <div className="mb-3 flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-oldglory-red/60" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-oldglory-red" /></span>
        <h2 className="font-display text-lg font-black tracking-tight text-navy"><T>Right Now</T></h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex items-center gap-3 rounded-xl border border-border bg-white p-4" data-testid="place-today-time">
          <Clock className="h-8 w-8 shrink-0 text-navy" strokeWidth={1.4} />
          <div className="min-w-0"><div className="font-display text-2xl font-black leading-none text-navy tabular-nums whitespace-nowrap">{timeStr || "—"}</div><div className="mt-1 truncate text-xs text-muted-foreground">{dateStr || <T>Local time</T>}</div></div>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-border bg-white p-4 sm:col-span-2" data-testid="place-today-weather">
          {wx ? (
            <><WIcon name={wx.icon} className="h-10 w-10 shrink-0 text-oldglory-red" /><div className="min-w-0 flex-1"><div className="flex items-baseline gap-2"><span className="font-display text-3xl font-black leading-none text-navy">{wx.temp_f}°</span><span className="truncate text-sm font-semibold text-ink"><T>{wx.condition}</T></span></div></div></>
          ) : <span className="text-sm text-muted-foreground">{loaded ? <T>Live weather unavailable.</T> : <T>Loading…</T>}</span>}
        </div>
      </div>
    </section>
  );
}
